import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, EmptyState, ErrorState, PageHeader, Panel, SelectField, Skeleton, cx } from "../components/ui.tsx";
import { FilterPanel } from "../components/nav-controls.tsx";
import { usePageTitle, useUrlState } from "../lib/nav.tsx";
import { AxisTag, StatusPill } from "../components/status.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { get } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { DIMENSION_LABEL, type StructureAxis, type Unit } from "../lib/types.ts";
import { Panorama } from "../components/panorama.tsx";
import { STATUS_LABEL, type InitiativeStatus } from "../../shared/planning.ts";
import { formatDateBR, formatInstantBR } from "../../shared/dates.ts";
import { axisLabel, initiativeLabel, krLabel, objectiveLabel } from "../../shared/numbering.ts";

interface Dashboard {
  counts: Record<InitiativeStatus, number>;
  total: number;
  initiatives: { id: string; title: string; status: InitiativeStatus; updated_at: string; axis_name: string; axis_color: string; unit_name: string | null; key_result_title: string; revision_number: number | null; number: number | null; kr_number: number | null }[];
  initiatives_total: number;
  axes: { id: string; name: string; color: string; editor_group_name: string | null; counts: Record<InitiativeStatus, number>; total: number; can_edit: boolean }[];
  pending_work: { changes_requested: number; drafts: number; awaiting_review?: number; reopen_requests?: number };
  agenda: { events: number; complete: number; incomplete: number };
  access_until: string;
}

const ORDER: InitiativeStatus[] = ["draft", "in_review", "changes_requested", "validated"];

/** T05 "Painel de Monitoramento": structural panorama with drill-down (feedback pt 3), then planning-state counts. */
export function DashboardPage() {
  const plan = usePlan();
  usePageTitle("Painel de Monitoramento", plan.name);
  const [f, setF] = useUrlState({ axis_id: "", unit_id: "", status: "", my_groups: "" });
  // The list and counters below follow the panorama level (feedback round 3, pt 14): eixo → objetivo → KR.
  const [drill, setDrill] = useUrlState({ pe: "", po: "", pk: "" });
  const scope = { axis_id: f.axis_id || drill.pe, objective_id: drill.pe ? drill.po : "", key_result_id: drill.pe && drill.po ? drill.pk : "" };
  const qs = new URLSearchParams(Object.entries({ ...f, ...scope }).filter(([, v]) => v)).toString();
  const q = useQuery({
    queryKey: planKey(plan.id, "dashboard", qs),
    queryFn: async () => (await get<{ data: Dashboard }>(`/plans/${plan.id}/dashboard?${qs}`)).data,
    placeholderData: keepPreviousData,
  });
  const structure = useQuery({
    queryKey: planKey(plan.id, "structure", false),
    queryFn: async () => (await get<{ data: StructureAxis[] }>(`/plans/${plan.id}/structure`)).data,
  });
  const units = useQuery({ queryKey: planKey(plan.id, "units"), queryFn: async () => (await get<{ data: Unit[] }>(`/plans/${plan.id}/units`)).data });
  const axesList = useQuery({ queryKey: planKey(plan.id, "axes-dash"), queryFn: async () => (await get<{ data: { id: string; name: string }[] }>(`/plans/${plan.id}/axes`)).data });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ [k]: e.target.value });
  const chips = [
    ...(f.axis_id ? [{ label: `Eixo: ${(axesList.data ?? []).find((a) => a.id === f.axis_id)?.name ?? "—"}`, onRemove: () => setF({ axis_id: "" }) }] : []),
    ...(f.unit_id ? [{ label: `Unidade: ${(units.data ?? []).find((u) => u.id === f.unit_id)?.name ?? "—"}`, onRemove: () => setF({ unit_id: "" }) }] : []),
    ...(f.status ? [{ label: STATUS_LABEL[f.status as InitiativeStatus], onRemove: () => setF({ status: "" }) }] : []),
    ...(f.my_groups ? [{ label: "Meus grupos", onRemove: () => setF({ my_groups: "" }) }] : []),
  ];
  const hasGroups = plan.membership.groups.length > 0;

  return (
    <>
      <PageHeader
        title="Painel de Monitoramento"
        subtitle={<>{plan.name} · {DIMENSION_LABEL} · Oficina de {formatDateBR(plan.starts_on)} a {formatDateBR(plan.ends_on)} · Participantes até {formatInstantBR(plan.access_until)} (horário de Brasília)</>}
      />
      {structure.isPending ? <Skeleton lines={4} /> : structure.isError ? <ErrorState error={structure.error} onRetry={() => void structure.refetch()} /> : (
        <Panorama planId={plan.id} axes={structure.data} />
      )}

      <h2 className="mb-2 text-lg font-bold">Situação do planejamento</h2>
      {drill.pe && structure.data && (() => {
        const ax = structure.data.find((a) => a.id === drill.pe);
        const ob = ax?.objectives.find((o) => o.id === drill.po);
        const kr = ob?.key_results.find((k) => k.id === drill.pk);
        const label = [ax && `${axisLabel(ax.number)}: ${ax.name}`, ob && objectiveLabel(ob.number), kr && krLabel(kr.number)].filter(Boolean).join(" › ");
        return (
          <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted">
            Mostrando apenas <span className="rounded-full bg-soft px-2.5 py-0.5 text-xs font-semibold text-soft-ink">{label}</span>
            <button type="button" className="elevate rounded-ctl px-2 py-1 text-xs font-semibold text-primary" onClick={() => setDrill({ pe: "", po: "", pk: "" }, { push: true })}>Ver o plano todo</button>
          </p>
        );
      })()}
      <FilterPanel chips={chips} onClear={() => setF({ axis_id: "", unit_id: "", status: "", my_groups: "" })}>
      <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField label="Eixo" value={f.axis_id} onChange={set("axis_id")}>
          <option value="">Todos</option>
          {(axesList.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectField>
        <SelectField label="Unidade" value={f.unit_id} onChange={set("unit_id")}>
          <option value="">Todas</option>
          {(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </SelectField>
        <SelectField label="Situação" value={f.status} onChange={set("status")}>
          <option value="">Todas</option>
          {ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </SelectField>
        <SelectField label="Grupos" value={f.my_groups} onChange={set("my_groups")} disabled={!hasGroups}
          hint={hasGroups ? undefined : "Você não faz parte de grupos."}>
          <option value="">Todos os eixos</option>
          <option value="true">Meus grupos</option>
        </SelectField>
      </div>
      </FilterPanel>

      {q.isPending ? <Skeleton lines={6} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : (
        <>
          {(q.data.pending_work.awaiting_review ?? 0) > 0 && (
            <Alert tone="info" title={`${q.data.pending_work.awaiting_review} iniciativa(s) aguardando sua revisão`}
              action={<GuardedLink href={`/planos/${plan.id}/revisoes`} className="tap font-semibold text-primary">Abrir fila de revisões</GuardedLink>} />
          )}
          {(q.data.pending_work.reopen_requests ?? 0) > 0 && (
            <Alert tone="warn" title={`${q.data.pending_work.reopen_requests} pedido(s) de reabertura aguardando sua decisão`}
              action={<GuardedLink href={`/planos/${plan.id}/revisoes#pedidos-reabertura`} className="tap font-semibold text-primary">Ver pedidos</GuardedLink>} />
          )}
          {q.data.pending_work.changes_requested > 0 && (
            <Alert tone="warn" title={`${q.data.pending_work.changes_requested} iniciativa(s) com ajustes solicitados nos eixos que você edita`} />
          )}
          <section aria-label="Iniciativas por situação" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {ORDER.map((s) => (
              <button key={s} type="button" aria-pressed={f.status === s} onClick={() => setF({ status: f.status === s ? "" : s })}
                className={cx(
                  "lift rounded-panel border bg-surface p-4 text-left",
                  f.status === s ? "border-primary ring-2 ring-accent/40" : "border-line",
                )}>
                <span className="eyebrow block">{STATUS_LABEL[s]}</span>
                <span className="num mt-1 block text-[30px] leading-tight font-medium">{q.data.counts[s]}</span>
              </button>
            ))}
            <button type="button" aria-pressed={!f.status} onClick={() => setF({ status: "" })}
              className={cx("lift col-span-2 rounded-panel border bg-surface p-4 text-left lg:col-span-1", !f.status ? "border-primary/60" : "border-line")}>
              <span className="eyebrow block">Total de iniciativas</span>
              <span className="num mt-1 block text-[30px] leading-tight font-medium">{q.data.total}</span>
            </button>
          </section>
          <p className="mt-2 text-xs text-muted">Situação do planejamento, não da execução. Cada iniciativa conta uma vez, pela sua situação atual. Clique em um número para filtrar a lista.</p>

          <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <section>
              <h2 className="mb-2 text-lg font-bold">Iniciativas {f.status && `· ${STATUS_LABEL[f.status as InitiativeStatus]}`}</h2>
              {q.data.initiatives.length === 0 ? (
                <EmptyState title="Nenhuma iniciativa corresponde aos filtros.">Crie iniciativas pelo Planejamento.</EmptyState>
              ) : (
                <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
                  {q.data.initiatives.map((i) => (
                    <li key={i.id}>
                      <GuardedLink href={`/planos/${plan.id}/iniciativas/${i.id}`} className="elevate block px-4 py-3">
                        <span className="block text-[11px] font-bold tracking-wide text-muted uppercase">{initiativeLabel(i.number)}</span>
                        <span className="flex flex-wrap items-center gap-2"><span className="font-semibold text-ink">{i.title}</span><StatusPill status={i.status} /></span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
                          <AxisTag name={i.axis_name} color={i.axis_color} /> · {krLabel(i.kr_number)}: {i.key_result_title} · {i.unit_name ?? "sem unidade"} · atualizada em {formatInstantBR(i.updated_at)}
                        </span>
                      </GuardedLink>
                    </li>
                  ))}
                </ul>
              )}
              {q.data.initiatives_total > q.data.initiatives.length && <p className="mt-2 text-xs text-muted">Mostrando as {q.data.initiatives.length} atualizadas mais recentemente de {q.data.initiatives_total}.</p>}
            </section>
            <aside className="space-y-4">
              <Panel>
                <h2 className="font-bold">Por eixo</h2>
                <ul className="mt-2 space-y-3">
                  {q.data.axes.map((a) => (
                    <li key={a.id} className="text-sm">
                      <AxisTag name={a.name} color={a.color} />
                      <p className="text-xs text-muted">{a.total} iniciativa(s) · {ORDER.filter((s) => a.counts[s]).map((s) => `${a.counts[s]} ${STATUS_LABEL[s].toLowerCase()}`).join(" · ") || "nenhuma"}</p>
                      <p className="text-xs text-muted">{a.editor_group_name ? `Grupo ${a.editor_group_name}` : "Somente instrutor"}{a.can_edit ? " · você edita" : ""}</p>
                    </li>
                  ))}
                  {q.data.axes.length === 0 && <li className="text-sm text-muted">Nenhum eixo.</li>}
                </ul>
              </Panel>
              <Panel>
                <h2 className="font-bold">Agenda</h2>
                <p className="mt-1 text-sm">{q.data.agenda.events} ação(ões) · {q.data.agenda.complete} com cadastro completo · {q.data.agenda.incomplete} em elaboração</p>
                <GuardedLink href={`/planos/${plan.id}/agenda`} className="tap mt-2 inline-flex text-sm font-semibold text-primary">Abrir agenda</GuardedLink>
              </Panel>
            </aside>
          </div>
        </>
      )}
    </>
  );
}
